'use client';

import { useEffect, useState, useRef } from 'react';
import { useRouter } from 'next/navigation';
import api from '@/lib/axios';
import useSepetStore from '@/store/sepet.store';
import useSiteStore from '@/store/site.store';
import toast from 'react-hot-toast';
import { ShoppingCart, Check, ChevronRight, ChevronLeft, Minus, Plus, Package, ZoomIn, X, Phone } from 'lucide-react';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import { useRouter as useNextRouter } from 'next/navigation';
import { API_URL } from '@/lib/config';
import ProductCard from '@/components/product/ProductCard';
import UrunGrubuSecici from '@/components/urun/UrunGrubuSecici';

const RESIM_BASE = API_URL.replace('/api', '');

interface Varyasyon {
  id: number; varyasyon_ad: string; varyasyon_kod: string;
  stok: number; satis_fiyati: number; piyasa_fiyati: number; aktif: boolean;
}

interface Urun {
  id: number; urun_ad: string; urun_aciklama: string; urun_kod: string;
  marka: string; urun_kdv: number; urun_slug: string;
  urun_olcu_en: number; urun_olcu_boy: number; urun_olcu_yukseklik: number;
  urun_grubu_id: number | null;
  kategori: { id: number; ad: string; slug: string; parent_id?: number | null };
  urun_varyasyonlari: Varyasyon[];
  urun_resimleri: { id: number; dosya_yolu: string; alt_metin: string; sira: number }[];
  takim_hammaddeleri_json?: any[];
}

interface KategoriZincir {
  id: number; ad: string; slug: string; parent_id: number | null;
}

interface GrupData {
  grup: {
    id: number;
    ad: string;
    slug: string;
    ozellikler: {
      id: number; kod: string; etiket: string;
      tip: string; birim: string | null; varyasyon_secici: boolean;
    }[];
  };
  urunler: {
    id: number;
    urun_slug: string | null;
    ozellik_degerleri: { ozellik_id: number; deger: string }[];
  }[];
  ozellik_secenekleri: Record<string, string[]>;
}

export default function UrunDetaySayfasi({
  urun: ilkUrun,
  grupData,
}: {
  urun: Urun;
  grupData: GrupData | null;
}) {
  const router = useRouter();
  const { ekle } = useSepetStore();
  const satis_kapali = useSiteStore((state) => state.satis_kapali);

  const [urun] = useState<Urun>(ilkUrun);
  const [seciliVaryasyon, setSeciliVaryasyon] = useState<Varyasyon | null>(
    ilkUrun.urun_varyasyonlari?.[0] ?? null
  );
  const [seciliResim, setSeciliResim] = useState(0);
  const [miktar, setMiktar] = useState(1);
  const [sepeteEklendi, setSepeteEklendi] = useState(false);
  const [zoomAcik, setZoomAcik] = useState(false);
  const [benzerUrunler, setBenzerUrunler] = useState<any[]>([]);
  const [kategoriZinciri, setKategoriZinciri] = useState<KategoriZincir[]>([]);
  const [kategoriUrunleri, setKategoriUrunleri] = useState<any[]>([]);
  const touchStartX = useRef<number>(0);
  const touchStartY = useRef<number>(0);

  useEffect(() => {
    api.get(`/urunler/${urun.id}/benzer`)
      .then(res => {
        const urunler = res.data || [];
        urunler.sort((a: any, b: any) => {
          if ((a.urun_olcu_en ?? 0) !== (b.urun_olcu_en ?? 0))
            return (a.urun_olcu_en ?? 0) - (b.urun_olcu_en ?? 0);
          if ((a.urun_olcu_boy ?? 0) !== (b.urun_olcu_boy ?? 0))
            return (a.urun_olcu_boy ?? 0) - (b.urun_olcu_boy ?? 0);
          return (a.urun_olcu_yukseklik ?? 0) - (b.urun_olcu_yukseklik ?? 0);
        });
        setBenzerUrunler(urunler);
      })
      .catch(() => {});

    if (urun.kategori?.id) {
      api.get(`/urunler/${urun.kategori.id}/kategori-zinciri`)
        .then(res => setKategoriZinciri(res.data || []))
        .catch(() => {});
    }

    // Grup yoksa kategori ürünlerini çek (eski dropdown)
    if (!urun.urun_grubu_id) {
      api.get(`/urunler/${urun.id}/kategori-urunleri`)
        .then(res => setKategoriUrunleri(res.data || []))
        .catch(() => {});
    }
  }, [urun.id]);

  const sepeteEkle = () => {
    if (!seciliVaryasyon) return;
    const mevcutSepetMiktar = useSepetStore.getState().kalemler.find(
      k => k.varyasyon_id === seciliVaryasyon.id
    )?.miktar || 0;
    const toplamMiktar = mevcutSepetMiktar + miktar;
    if (toplamMiktar > seciliVaryasyon.stok) {
      toast.error(`Stokta sadece ${seciliVaryasyon.stok} adet var!`, { duration: 2000 });
      return;
    }
    ekle(seciliVaryasyon.id, miktar);
    setSepeteEklendi(true);
    toast.success(`${miktar} ürün sepete eklendi!`, { duration: 2000 });
    setTimeout(() => setSepeteEklendi(false), 2000);
  };

  const sonrakiResim = () => setSeciliResim(prev => (prev + 1) % urun.urun_resimleri.length);
  const oncekiResim = () => setSeciliResim(prev => (prev - 1 + urun.urun_resimleri.length) % urun.urun_resimleri.length);

  const touchBaslat = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const touchBirak = (e: React.TouchEvent) => {
    const diffX = touchStartX.current - e.changedTouches[0].clientX;
    const diffY = Math.abs(touchStartY.current - e.changedTouches[0].clientY);
    if (Math.abs(diffX) > 40 && diffY < 60) {
      if (diffX > 0) sonrakiResim(); else oncekiResim();
    }
  };

  const urunUrl = (u: any) => u.urun_slug ? `/${u.urun_slug}` : `/urunler/${u.id}`;

  const fiyatVar = seciliVaryasyon && Number(seciliVaryasyon.satis_fiyati) > 0;
  const kdvDahilFiyat = fiyatVar
    ? Math.ceil(Number(seciliVaryasyon!.satis_fiyati) * (1 + (urun.urun_kdv || 20) / 100))
    : 0;
  const indirimVar = fiyatVar && seciliVaryasyon?.piyasa_fiyati != null && seciliVaryasyon.piyasa_fiyati > seciliVaryasyon.satis_fiyati;
  const indirimOrani = indirimVar
    ? Math.round(((seciliVaryasyon!.piyasa_fiyati - seciliVaryasyon!.satis_fiyati) / seciliVaryasyon!.piyasa_fiyati) * 100)
    : 0;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1 text-sm text-[var(--color-gray-500)] mb-6 flex-wrap">
        <span className="hover:text-[var(--color-primary)] cursor-pointer" onClick={() => router.push('/')}>Ana Sayfa</span>
        {kategoriZinciri.map((kat) => (
          <span key={kat.id} className="flex items-center gap-1">
            <ChevronRight className="w-4 h-4" />
            <span className="hover:text-[var(--color-primary)] cursor-pointer"
              onClick={() => router.push(`/${kat.slug}`)}>
              {kat.ad}
            </span>
          </span>
        ))}
        <ChevronRight className="w-4 h-4" />
        <span className="text-[var(--color-gray-800)] font-medium">{urun.urun_ad}</span>
      </nav>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        {/* Sol — Resimler */}
        <div>
          <div
            className="aspect-square bg-[var(--color-gray-100)] rounded-[var(--radius-xl)] overflow-hidden mb-3 relative cursor-zoom-in select-none"
            onClick={() => urun.urun_resimleri?.length > 0 && setZoomAcik(true)}
            onTouchStart={touchBaslat}
            onTouchEnd={touchBirak}
          >
            {indirimVar && (
              <div className="absolute top-3 left-3 z-10"><Badge variant="error">-%{indirimOrani}</Badge></div>
            )}
            {urun.urun_resimleri?.length > 1 && (
              <>
                <button onClick={(e) => { e.stopPropagation(); oncekiResim(); }}
                  className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow transition">
                  <ChevronLeft className="w-5 h-5 text-gray-600" />
                </button>
                <button onClick={(e) => { e.stopPropagation(); sonrakiResim(); }}
                  className="absolute right-10 top-1/2 -translate-y-1/2 z-10 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow transition">
                  <ChevronRight className="w-5 h-5 text-gray-600" />
                </button>
              </>
            )}
            {urun.urun_resimleri?.length > 0 && (
              <div className="absolute top-3 right-3 z-10 bg-white bg-opacity-80 rounded-full p-1.5 shadow">
                <ZoomIn className="w-4 h-4 text-[var(--color-gray-600)]" />
              </div>
            )}
            {urun.urun_resimleri?.[seciliResim] ? (
              <img src={`${RESIM_BASE}${urun.urun_resimleri[seciliResim].dosya_yolu}`}
                alt={urun.urun_resimleri[seciliResim].alt_metin || urun.urun_ad}
                className="w-full h-full object-cover transition-all duration-300" />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-[var(--color-gray-400)] gap-2">
                <Package className="w-16 h-16" /><span>Resim Yok</span>
              </div>
            )}
          </div>

          {urun.urun_resimleri?.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {urun.urun_resimleri.map((resim, i) => (
                <button key={resim.id} onClick={() => setSeciliResim(i)} onMouseEnter={() => setSeciliResim(i)}
                  className={`flex-shrink-0 w-16 h-16 rounded-[var(--radius-md)] overflow-hidden border-2 transition-all ${
                    seciliResim === i ? 'border-[var(--color-primary)] shadow-md' : 'border-[var(--color-gray-200)] hover:border-[var(--color-gray-400)]'
                  }`}>
                  <img src={`${RESIM_BASE}${resim.dosya_yolu}`} alt={resim.alt_metin || ''} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Sağ — Bilgiler */}
        <div>
          <p className="text-xs text-[var(--color-gray-400)] uppercase tracking-wide mb-1">{urun.marka}</p>
          <h1 className="text-2xl font-bold text-[var(--color-gray-800)] mb-2">{urun.urun_ad}</h1>
          {urun.takim_hammaddeleri_json && (
            <pre className="text-xs bg-gray-100 p-2 rounded mb-4 overflow-auto">
              {JSON.stringify(urun.takim_hammaddeleri_json, null, 2)}
            </pre>
          )}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {urun.kategori && <Badge variant="primary" size="sm">{urun.kategori.ad}</Badge>}
            <span className="text-xs text-[var(--color-gray-400)]">Kod: {urun.urun_kod}</span>
          </div>

          {seciliVaryasyon && (
            <div className="bg-[var(--color-gray-50)] rounded-[var(--radius-xl)] p-4 mb-6">
              <div className="flex items-end gap-3 mb-1">
                <p className="text-3xl font-bold text-[var(--color-primary)]">
                  {fiyatVar ? Number(seciliVaryasyon.satis_fiyati).toLocaleString('tr-TR') + ' ₺' : 'Fiyat Sorunuz'}
                </p>
                {indirimVar && (
                  <p className="text-[var(--color-gray-400)] line-through text-lg mb-0.5">
                    {Number(seciliVaryasyon.piyasa_fiyati).toLocaleString('tr-TR')} ₺
                  </p>
                )}
              </div>
              {fiyatVar && (
                <p className="text-sm text-[var(--color-gray-500)]">
                  KDV Dahil: <span className="font-medium text-[var(--color-gray-700)]">{kdvDahilFiyat.toLocaleString('tr-TR')} ₺</span>
                  <span className="ml-2 text-xs">(KDV %{urun.urun_kdv})</span>
                </p>
              )}
            </div>
          )}

          {/* ── Grup Seçici (grup varsa) ── */}
          {grupData && (
            <div className="mb-6">
              <UrunGrubuSecici
                ozellikler={grupData.grup.ozellikler}
                ozellikSecenekleri={grupData.ozellik_secenekleri}
                urunler={grupData.urunler}
                aktifUrunSlug={urun.urun_slug}
              />
            </div>
          )}

          {/* ── Eski Kategori Dropdown (grup yoksa) ── */}
          {!grupData && kategoriUrunleri.some(u => u.urun_olcu_en || u.urun_olcu_boy || u.urun_olcu_yukseklik) && (
            <div className="mb-6">
              <p className="text-sm font-semibold text-[var(--color-gray-700)] mb-2">Boyut Seçiniz:</p>
              <select
                value={urun.urun_slug}
                onChange={e => router.push(`/${e.target.value}`)}
                className="w-full border border-[var(--color-gray-300)] rounded-[var(--radius-lg)] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] bg-white"
              >
                {kategoriUrunleri
                  .filter(u => u.urun_olcu_en || u.urun_olcu_boy || u.urun_olcu_yukseklik)
                  .map(u => {
                    const olcu = [u.urun_olcu_en, u.urun_olcu_boy, u.urun_olcu_yukseklik]
                      .filter(Boolean).join('x');
                    return (
                      <option key={u.id} value={u.urun_slug}>{olcu} cm</option>
                    );
                  })}
              </select>
            </div>
          )}

          {/* Varyasyon Seçenekleri */}
          {urun.urun_varyasyonlari?.length > 1 && (
            <div className="mb-6">
              <p className="text-sm font-semibold text-[var(--color-gray-700)] mb-2">Seçenekler:</p>
              <div className="flex flex-wrap gap-2">
                {urun.urun_varyasyonlari.map((v) => (
                  <button key={v.id} onClick={() => setSeciliVaryasyon(v)} disabled={v.stok === 0}
                    className={`px-4 py-2 border rounded-[var(--radius-lg)] text-sm transition ${
                      seciliVaryasyon?.id === v.id
                        ? 'border-[var(--color-primary)] bg-[var(--color-info-light)] text-[var(--color-primary)] font-medium'
                        : v.stok === 0
                        ? 'border-[var(--color-gray-200)] text-[var(--color-gray-400)] cursor-not-allowed line-through'
                        : 'border-[var(--color-gray-300)] hover:border-[var(--color-primary)] text-[var(--color-gray-700)]'
                    }`}>
                    {v.varyasyon_ad}{v.stok === 0 && ' (Tükendi)'}
                  </button>
                ))}
              </div>
            </div>
          )}

          {seciliVaryasyon && (
            <div className="mb-4">
              {seciliVaryasyon.stok > 0
                ? <Badge variant="success">✓ Stokta — {seciliVaryasyon.stok} adet</Badge>
                : <Badge variant="error">Stok Tükendi</Badge>}
            </div>
          )}

          {fiyatVar && (
            <div className="mb-6">
              <p className="text-sm font-semibold text-[var(--color-gray-700)] mb-2">Adet:</p>
              <div className="flex items-center gap-3">
                <button onClick={() => setMiktar(Math.max(1, miktar - 1))}
                  className="w-9 h-9 border border-[var(--color-gray-300)] rounded-[var(--radius-md)] flex items-center justify-center hover:bg-[var(--color-gray-100)] transition">
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-10 text-center font-bold text-lg">{miktar}</span>
                <button onClick={() => {
                  const mevcutSepetMiktar = useSepetStore.getState().kalemler.find(
                    k => k.varyasyon_id === seciliVaryasyon?.id
                  )?.miktar || 0;
                  const maxMiktar = (seciliVaryasyon?.stok || 1) - mevcutSepetMiktar;
                  if (miktar >= maxMiktar) {
                    toast.error(`Sepetinizde zaten ${mevcutSepetMiktar} adet var, en fazla ${seciliVaryasyon?.stok} adet ekleyebilirsiniz!`, { duration: 3000 });
                    return;
                  }
                  setMiktar(miktar + 1);
                }}
                  className="w-9 h-9 border border-[var(--color-gray-300)] rounded-[var(--radius-md)] flex items-center justify-center hover:bg-[var(--color-gray-100)] transition">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {satis_kapali ? (
            <Button fullWidth size="lg" variant="outline" disabled>
              <Phone className="w-5 h-5" /> Fiyat Sorunuz
            </Button>
          ) : fiyatVar ? (
            <Button fullWidth size="lg" variant={sepeteEklendi ? 'secondary' : 'primary'}
              onClick={sepeteEkle} disabled={!seciliVaryasyon || seciliVaryasyon.stok === 0}>
              {sepeteEklendi ? <><Check className="w-5 h-5" /> Sepete Eklendi!</> : <><ShoppingCart className="w-5 h-5" /> Sepete Ekle</>}
            </Button>
          ) : (
            <Button fullWidth size="lg" variant="outline">
              <Phone className="w-5 h-5" /> Fiyat Sorunuz
            </Button>
          )}

          {(urun.urun_olcu_en || urun.urun_olcu_boy || urun.urun_olcu_yukseklik) && (
            <div className="mt-6 border-t border-[var(--color-gray-200)] pt-4">
              <h3 className="text-sm font-semibold text-[var(--color-gray-700)] mb-3">Ürün Ölçüleri</h3>
              <div className="grid grid-cols-3 gap-3">
                {urun.urun_olcu_en && <div className="text-center bg-[var(--color-gray-50)] rounded-[var(--radius-lg)] p-3"><p className="text-xs text-[var(--color-gray-500)]">En</p><p className="font-bold text-[var(--color-gray-800)]">{urun.urun_olcu_en} cm</p></div>}
                {urun.urun_olcu_boy && <div className="text-center bg-[var(--color-gray-50)] rounded-[var(--radius-lg)] p-3"><p className="text-xs text-[var(--color-gray-500)]">Boy</p><p className="font-bold text-[var(--color-gray-800)]">{urun.urun_olcu_boy} cm</p></div>}
                {urun.urun_olcu_yukseklik && <div className="text-center bg-[var(--color-gray-50)] rounded-[var(--radius-lg)] p-3"><p className="text-xs text-[var(--color-gray-500)]">Yükseklik</p><p className="font-bold text-[var(--color-gray-800)]">{urun.urun_olcu_yukseklik} cm</p></div>}
              </div>
            </div>
          )}

          {urun.urun_aciklama && (
            <div className="mt-6 border-t border-[var(--color-gray-200)] pt-4">
              <h3 className="text-base font-bold text-[var(--color-gray-800)] mb-3">Ürün Açıklaması</h3>
              <div className="text-sm text-[var(--color-gray-600)] leading-relaxed
                [&_h2]:text-base [&_h2]:font-bold [&_h2]:text-gray-800 [&_h2]:mb-2 [&_h2]:mt-4
                [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-gray-700 [&_h3]:mb-1 [&_h3]:mt-3
                [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2
                [&_li]:mb-1 [&_p]:mb-2 [&_strong]:font-semibold [&_strong]:text-gray-800 [&_em]:italic"
                dangerouslySetInnerHTML={{ __html: urun.urun_aciklama }} />
            </div>
          )}
        </div>
      </div>

      {benzerUrunler.length > 0 && (
        <div className="mt-12">
          <h2 className="text-xl font-bold text-[var(--color-gray-800)] mb-6">Benzer Ürünler</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {benzerUrunler.map((benzer) => (
              <ProductCard
                key={benzer.id}
                id={benzer.id}
                urun_ad={benzer.urun_ad}
                marka={benzer.marka}
                resimler={benzer.urun_resimleri}
                varyasyonlar={benzer.urun_varyasyonlari}
                urun_kdv={benzer.urun_kdv}
                href={urunUrl(benzer)}
              />
            ))}
          </div>
        </div>
      )}

      {zoomAcik && urun.urun_resimleri?.[seciliResim] && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4" onClick={() => setZoomAcik(false)}>
          <button className="absolute top-4 right-4 text-white p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition" onClick={() => setZoomAcik(false)}>
            <X className="w-6 h-6" />
          </button>
          {seciliResim > 0 && (
            <button className="absolute left-4 text-white p-3 hover:bg-white hover:bg-opacity-20 rounded-full transition"
              onClick={(e) => { e.stopPropagation(); setSeciliResim(seciliResim - 1); }}>
              <ChevronLeft className="w-8 h-8" />
            </button>
          )}
          <img src={`${RESIM_BASE}${urun.urun_resimleri[seciliResim].dosya_yolu}`} alt={urun.urun_ad}
            className="max-h-[90vh] max-w-[90vw] object-contain rounded-[var(--radius-lg)]" onClick={(e) => e.stopPropagation()} />
          {seciliResim < urun.urun_resimleri.length - 1 && (
            <button className="absolute right-4 text-white p-3 hover:bg-white hover:bg-opacity-20 rounded-full transition"
              onClick={(e) => { e.stopPropagation(); setSeciliResim(seciliResim + 1); }}>
              <ChevronRight className="w-8 h-8" />
            </button>
          )}
          <div className="absolute bottom-4 text-white text-sm bg-black bg-opacity-50 px-3 py-1 rounded-full">
            {seciliResim + 1} / {urun.urun_resimleri.length}
          </div>
        </div>
      )}
    </div>
  );
}